/**
 * Format: Product
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API product responses.
 *
 */
import { formatImages } from '~/format/format-image'
import { formatMedia } from '~/format/format-media'
import { formatOptions } from '~/format/format-option'
import { formatProductPriceRange } from '~/format/format-price'
import { formatVariants } from '~/format/format-variant'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Format product.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Product
 * @param {Object} response - GraphQL response.
 * @param {Object} [collection] - Formatted collection object.
 * @returns {Object}
 */
export function formatProduct(response, collection) {
  const product = response.product ? response.product : response

  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatProductPriceRange(product.compareAtPriceRange),
    description: product.descriptionHtml,
    giftCard: product.giftCard,
    handle: product.handle,
    id: decodeApiId(product.id),
    media: formatMedia(product),
    price: formatProductPriceRange(product.priceRange),
    tags: product.tags,
    title: product.title,
    type: product.type,
    variants: formatVariants(product, collection),
    vendor: product.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  const featuredImages = formatImages(product, transformEdges(product.featuredImages))

  formattedProduct.featuredImage = featuredImages.length
    ? featuredImages[0]
    : featuredImages

  formattedProduct.secondaryImage = featuredImages.length > 1
    ? featuredImages[1]
    : {}

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  formattedProduct.options = formatOptions(product, formattedProduct.variants)

  formattedProduct.selectedOrFirstAvailableVariant =
    getSelectedOrFirstAvailableVariant(formattedProduct)

  const url = collection
    ? `${cnvs.routes.normalisedRoot}collections/${collection.handle}/products/${product.handle}`
    : `${cnvs.routes.normalisedRoot}products/${product.handle}`

  formattedProduct.url = formatUrl(url, product.trackingParameters)

  return formattedProduct
}

/**
 * Get selected or first available variant.
 * - 'Selected' based on URL parameter.
 * - If no variants available then return first.
 * @param {Object} product - Formatted product object.
 * @returns {Object}
 */
function getSelectedOrFirstAvailableVariant(product) {
  const urlSearchParams = new URLSearchParams(location.search)

  const variantId = urlSearchParams.has('variant')
    ? urlSearchParams.get('variant')
    : false

  const selectedVariant = product.variants.find((variant) => {
    if (variantId) {
      return variant.id === Number(variantId)
    }

    return variant.available
  })

  if (selectedVariant) {
    return selectedVariant
  }

  return product.variants[0]
}

/**
 * Format response URL with tracking parameters.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Product#field-product-trackingparameters
 * @param {String} url - The URL of the object.
 * @param {Null|String} [trackingParameters] - Response tracking parameters.
 * @returns {String}
 */
export function formatUrl(url, trackingParameters) {
  if (!trackingParameters) {
    return url
  }

  /**
   * Storefront API doesn't include https in URL but new URL() requires it.
   */
  const localUrl = `https://${url.split('//')[1]}`
  const urlObject = new URL(localUrl)
  const trackingSearchParams = new URLSearchParams(trackingParameters)

  const parameters = new URLSearchParams({
    ...Object.fromEntries(urlObject.searchParams),
    ...Object.fromEntries(trackingSearchParams),
  })

  if (!parameters.size) {
    return url
  }

  const href = urlObject.href
    .split('?')[0]
    .replace('https:', '')

  return `${href}?${parameters.toString()}`
}
